import type { ReactNode } from 'react';
import type { IssueCard } from '@tasks/contracts';

import { Avatar } from '../../components/Avatar';
import { Badge } from '../../components/Badge';
import { IconButton } from '../../components/IconButton';
import { Tooltip } from '../../components/Tooltip';
import { PRIORITY_COLOR_VAR } from '../../lib/types';
import { formatPoints } from '../../lib/format';
import { priorityLabels } from '../../i18n/enums';
import { cn } from '../../lib/cn';
import { CircleDashed, Paperclip, MessageSquare, GitBranch, GripVertical } from 'lucide-react';
import styles from './IssueCardView.module.css';

export type IssueCardViewProps = {
  readonly issue: IssueCard;
  readonly onOpen?: (key: string) => void;
  readonly isSelected?: boolean;
  readonly isDragging?: boolean;
  /** Узел-источник перетаскивания (@dnd-kit). Без него карточка статична. */
  readonly dragNodeRef?: (node: HTMLElement | null) => void;
  /** Атрибуты и обработчики @dnd-kit, делающие карточку перетаскиваемой. */
  readonly dragProps?: Record<string, unknown>;
  readonly isOverdue?: boolean;
  /** Дополнительные бейджи в строке ключа (например, статус на «Моей доске»). */
  readonly extra?: ReactNode;
};

/** Компактная карточка доски (docs/06 §3.1): ключ, заголовок 2 строки, мета. */
export function IssueCardView({
  issue,
  onOpen,
  isSelected = false,
  isDragging = false,
  dragNodeRef,
  dragProps,
  isOverdue,
  extra,
}: IssueCardViewProps) {
  const overdue = isOverdue ?? issue.isOverdue;

  return (
    <article
      ref={dragNodeRef}
      {...dragProps}
      className={cn(styles['card'], isSelected && styles['selected'], isDragging && styles['dragging'])}
      aria-label={`${issue.key} ${issue.summary}`}
      tabIndex={0}
      data-issue-card="true"
      data-testid={`card-${issue.key}`}
    >
      <div className={styles['top']}>
        <span className={styles['key']}>{issue.key}</span>
        {extra}
        {dragProps ? (
          <span aria-hidden className={styles['handle']}>
            <GripVertical size={14} />
          </span>
        ) : null}
        {onOpen ? (
          <Tooltip content="Открыть задачу">
            <IconButton
              size="sm"
              label={`Открыть ${issue.key}`}
              icon={<GitBranch size={13} />}
              onClick={() => onOpen(issue.key)}
            />
          </Tooltip>
        ) : null}
      </div>

      <button type="button" className={styles['summaryButton']} onClick={() => onOpen?.(issue.key)}>
        <span className={styles['summary']}>{issue.summary}</span>
      </button>

      {issue.epic ? (
        <span className={styles['epic']} title={`Эпик ${issue.epic.key}: ${issue.epic.summary}`}>
          <span aria-hidden className={styles['epicDot']} />
          {issue.epic.summary}
        </span>
      ) : null}

      {issue.labels.length > 0 ? (
        <ul className={styles['labels']}>
          {issue.labels.slice(0, 3).map((label) => (
            <li key={label}>
              <Badge>{label}</Badge>
            </li>
          ))}
          {issue.labels.length > 3 ? <li className={styles['more']}>+{issue.labels.length - 3}</li> : null}
        </ul>
      ) : null}

      <div className={styles['meta']}>
        <Tooltip content={`Приоритет: ${priorityLabels[issue.priority]}`}>
          <span aria-label={`Приоритет: ${priorityLabels[issue.priority]}`} className={styles['priority']}>
            <span aria-hidden className={styles['priorityDot']} style={{ background: PRIORITY_COLOR_VAR[issue.priority] }} />
            {priorityLabels[issue.priority]}
          </span>
        </Tooltip>

        {issue.storyPoints === null ? null : <span className={styles['points']}>{formatPoints(issue.storyPoints)} sp</span>}

        {issue.subtasks.total > 0 ? (
          <span className={styles['indicator']} title="Подзадачи">
            <CircleDashed aria-hidden size={12} />
            {issue.subtasks.done}/{issue.subtasks.total}
          </span>
        ) : null}

        {issue.hasAttachments ? (
          <span className={styles['indicator']} title="Есть вложения">
            <Paperclip aria-hidden size={12} />
          </span>
        ) : null}

        {issue.commentsCount > 0 ? (
          <span className={styles['indicator']} title="Комментарии">
            <MessageSquare aria-hidden size={12} />
            {issue.commentsCount}
          </span>
        ) : null}

        <span className={styles['spacer']} />

        {issue.dueDate ? (
          <span className={cn(styles['due'], overdue && styles['overdue'])} title="Срок">
            {issue.dueDate.slice(5)}
          </span>
        ) : null}

        <Avatar user={issue.assignee} size="xs" />
      </div>
    </article>
  );
}
